import { IFormField } from '@opengov/forms-library/types/form';
import { FIELD_FORMATTERS } from '@opengov/forms-library/utils/formUtils';
import {
  FormattingOptions,
  getNumberFormatters,
  NumberFormatters
} from '@opengov/forms-library/utils/numberFormattingUtils';

import cheerio from 'cheerio';
import { isNil, keys, values } from 'lodash';

import { IAllProposalDetails, IBudget, IMergeTags, IStoryContent } from '../types';

export const DATA_MERGE_TAG_ID = 'data-merge-tag-id';

export const MERGE_TAG = 'merge-tag';

const formatFormFieldUserInputValue = (field: IFormField, numberFormatters: NumberFormatters) => {
  const { formFieldEntry } = field;
  if (!isNil(formFieldEntry.userInputValue)) {
    const fieldFormatter = FIELD_FORMATTERS[field.type];
    const formattedValue = fieldFormatter
      ? fieldFormatter(formFieldEntry.userInputValue, field, numberFormatters)
      : formFieldEntry.userInputValue;
    return `${formattedValue}`;
  }
  return null;
};

const getMergeTagValue = (fieldlabel: string, proposalTitle: string) =>
  `${fieldlabel.toLowerCase().replace(/\s/g, '_')}:${proposalTitle.toLowerCase().replace(/\s/g, '_')}`;

export const getMergeTagId = ({
  budgetId,
  proposalId,
  fieldId
}: {
  budgetId: number;
  proposalId: number;
  fieldId: string;
}) => btoa(`${budgetId},${proposalId},${fieldId}`);

export const convertProposalsFieldsToMergeTags = (
  proposalsFields: IAllProposalDetails,
  storyBudget: IBudget
): IMergeTags => {
  const { budgetId, preferences } = storyBudget;
  const numberFormatters = getNumberFormatters(preferences?.presentation_scale?.amount ?? FormattingOptions.one);
  const mergeTags: IMergeTags = {};
  values(proposalsFields).forEach((proposal) => {
    if (proposal) {
      const { id: proposalId, title: proposalTitle } = proposal;

      proposal.proposalFields.forEach((field) => {
        const { fieldTemplateId: fieldId, label } = field;
        const mergeTagId = getMergeTagId({ budgetId, proposalId, fieldId });
        const value = getMergeTagValue(label, proposalTitle);
        const content = formatFormFieldUserInputValue(field, numberFormatters);

        mergeTags[mergeTagId] = {
          id: mergeTagId,
          proposalId,
          fieldId,
          label,
          description: proposalTitle,
          value,
          content
        };
      });
    }
  });
  return mergeTags;
};

export const replaceMergeTagsWithValues = (
  content: string,
  mergeTags: IMergeTags,
  isGetInnerHtml: boolean = false
): string => {
  const $ = cheerio.load(content, {
    xmlMode: false,
    decodeEntities: false
  });
  $(`.${MERGE_TAG}`).each((index, elem) => {
    const mergeTagId = elem.attribs[DATA_MERGE_TAG_ID];
    $(elem).replaceWith(`${mergeTags[mergeTagId]?.content ?? ''}`);
  });
  return isGetInnerHtml ? $('body').html() : $.html();
};

export const replaceStoryContentsWithMergeTagValues = (storyContents: IStoryContent[], mergeTags: IMergeTags) => {
  let contents = storyContents;
  if (mergeTags && keys(mergeTags).length) {
    contents = storyContents.map((storyContent) => {
      storyContent.content = replaceMergeTagsWithValues(storyContent.content, mergeTags, true);
      if (storyContent.contentHtml)
        storyContent.contentHtml = replaceMergeTagsWithValues(storyContent.contentHtml, mergeTags, true);
      return storyContent;
    });
  }
  return contents;
};
